import { safeJson } from "src/app/helpers/Index";
import { faLayerGroup, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { SeasonService, TicketTemplateService } from "src/app/services/Index";
import "./AdminPricing.css";

type Props = {};
type State = {
	available: boolean;
	isLoading: boolean;
	saving: boolean;
	templates: any[];
	seasons: any[];
	selectedId: number | null;
	grid: any;
	/** Edits held locally until saved — the grid is a spreadsheet, not a form. */
	dirty: { [key: string]: { pricePence: number; disabled: boolean } };
	showDialog: boolean;
	edit: any;
};

const key = (areaId: number, typeId: number) => `${areaId}:${typeId}`;
const toPounds = (pence: any) => (pence == null ? null : Number(pence) / 100);

/**
 * Ticket pricing.
 *
 * The grid is areas down, ticket types across, for one price category. Editing
 * happens in the cells and saves in a batch — a club prices a whole column at
 * once ("Adult goes up a pound"), and saving per keystroke would leave the grid
 * half-changed if one call failed.
 */
export class AdminPricing extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = {
			available: true, isLoading: true, saving: false, templates: [], seasons: [],
			selectedId: null, grid: null, dirty: {}, showDialog: false, edit: null
		};
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const [res, seasons] = await Promise.all([
				(await TicketTemplateService.getAll()).json(),
				(await SeasonService.getAll()).json()
			]);
			if (res?.available === false) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			const templates = res?.templates || [];
			this.setState({
				templates,
				seasons: Array.isArray(seasons) ? seasons : [],
				isLoading: false
			});
			if (templates.length) this.selectTemplate(templates[0].id);
		} catch {
			this.setState({ available: false, isLoading: false });
		}
	}

	async selectTemplate(id: number) {
		if (Object.keys(this.state.dirty).length &&
			!window.confirm("You've got unsaved prices. Switch category and lose them?")) return;

		this.setState({ selectedId: id, grid: null, dirty: {} });
		try {
			const grid = await safeJson(TicketTemplateService.getGrid(id));
			this.setState({ grid });
		} catch { /* leave null */ }
	}

	/** Current value for a cell: the local edit if there is one, else the saved value. */
	cellValue(areaId: number, typeId: number, saved: any) {
		const edit = this.state.dirty[key(areaId, typeId)];
		if (edit) return edit;
		return { disabled: saved?.disabled ?? false, pricePence: saved?.pricePence ?? null };
	}

	setCell(areaId: number, typeId: number, patch: any, saved: any) {
		const current = this.cellValue(areaId, typeId, saved);
		this.setState({
			dirty: {
				...this.state.dirty,
				[key(areaId, typeId)]: { ...current, ...patch }
			}
		});
	}

	async saveGrid() {
		const { selectedId, dirty } = this.state;
		if (!selectedId || !Object.keys(dirty).length) return;

		this.setState({ saving: true });
		const cells = Object.entries(dirty).map(([k, value]) => {
			const [areaId, typeId] = k.split(":").map(Number);
			return {
				disabled: !!value.disabled,
				pricePence: Math.round(Number(value.pricePence) || 0),
				ticketTypeId: typeId,
				venueAreaId: areaId
			};
		});

		const res = await TicketTemplateService.saveGrid(selectedId, cells);
		this.setState({ saving: false });

		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't save prices" });
			return;
		}
		this.toast.show({ closable: false, detail: `${cells.length} price${cells.length === 1 ? "" : "s"} saved.`, life: 3000, severity: "success", summary: "Prices updated" });
		this.setState({ dirty: {} });
		this.selectTemplate(selectedId);
	}

	async copyFrom(sourceId: number) {
		const { selectedId } = this.state;
		if (!selectedId) return;
		const res = await TicketTemplateService.copyFrom(selectedId, sourceId);
		if (!res.ok) {
			const message = await res.text();
			this.toast.show({ closable: false, detail: message || "Please try again later.", life: 5000, severity: "error", summary: "Couldn't copy" });
			return;
		}
		const body = await res.json();
		this.toast.show({ closable: false, detail: `${body.copied} prices copied. Adjust and save.`, life: 4000, severity: "success", summary: "Copied" });
		this.selectTemplate(selectedId);
	}

	async saveTemplate() {
		const t = this.state.edit;
		if (!t?.name?.trim()) return;
		this.setState({ saving: true });
		const payload = { isDefault: !!t.isDefault, name: t.name.trim(), seasonId: t.seasonId || null, sortOrder: Number(t.sortOrder) || 0 };
		const res = t.id
			? await TicketTemplateService.update(t.id, payload)
			: await TicketTemplateService.add(payload);
		this.setState({ saving: false });
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't save" });
			return;
		}
		this.setState({ edit: null, showDialog: false });
		this.load();
	}

	async removeTemplate(template: any) {
		if (!window.confirm(`Remove "${template.name}"? Fixtures already priced from it keep working.`)) return;
		await TicketTemplateService.remove(template.id);
		this.setState({ selectedId: null, grid: null, dirty: {} });
		this.load();
	}

	renderGrid() {
		const { grid, dirty } = this.state;
		if (!grid) return <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>;

		if (!grid.areas?.length || !grid.ticketTypes?.length) {
			return (
				<div className="pr-empty">
					<FontAwesomeIcon icon={faLayerGroup} />
					<h3>Nothing to price yet</h3>
					<p>
						{!grid.areas?.length && "Set up your venue areas first, under Venues & Seating. "}
						{!grid.ticketTypes?.length && "Add some ticket types under Ticketing. "}
						A price is an area crossed with a ticket type, so you need both.
					</p>
				</div>
			);
		}

		return (
			<>
				<div className="pr-gridwrap">
					<table className="pr-grid">
						<thead>
							<tr>
								<th>Area</th>
								{grid.ticketTypes.map((t: any) => (
									<th key={`h-${t.id}`}>{t.name}{t.isConcession && <small>concession</small>}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{grid.areas.map((area: any) => (
								<tr key={`r-${area.id}`}>
									<th>
										{area.name}
										{area.isUnallocated && <small>standing</small>}
									</th>
									{area.prices.map((saved: any) => {
										const value = this.cellValue(area.id, saved.ticketTypeId, saved);
										const isDirty = !!dirty[key(area.id, saved.ticketTypeId)];
										const unset = !saved.isSet && !isDirty;
										return (
											<td className={`${isDirty ? "dirty" : ""} ${unset ? "unset" : ""}`} key={`c-${area.id}-${saved.ticketTypeId}`}>
												<InputNumber
													currency="GBP"
													disabled={value.disabled}
													inputStyle={{ textAlign: "right", width: "6.2rem" }}
													locale="en-GB"
													mode="currency"
													onValueChange={(e) => this.setCell(area.id, saved.ticketTypeId, { pricePence: Math.round((e.value || 0) * 100) }, saved)}
													value={toPounds(value.pricePence)}
												/>
												<button
													className={`pr-toggle${value.disabled ? " off" : ""}`}
													onClick={() => this.setCell(area.id, saved.ticketTypeId, { disabled: !value.disabled }, saved)}
													title={value.disabled ? "Not sold here — click to offer it" : "On sale — click to stop selling it here"}
													type="button"
												>
													{value.disabled ? "not sold" : "on sale"}
												</button>
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<p className="pr-legend">
					A cell with no price has never been set &mdash; that&rsquo;s different from a price of £0, which is a real free ticket.
				</p>
			</>
		);
	}

	render() {
		const { available, isLoading, templates, seasons, selectedId, dirty } = this.state;
		const dirtyCount = Object.keys(dirty).length;
		const selected = templates.find((t) => t.id === selectedId);

		if (!isLoading && !available) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>Ticket pricing</h2>
					<div className="pr-empty">
						<FontAwesomeIcon icon={faLayerGroup} />
						<h3>Pricing isn&rsquo;t switched on yet</h3>
						<p>The price category tables haven&rsquo;t been created on this environment.</p>
					</div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 6 }}>Ticket pricing</h2>
				<p style={{ color: "var(--pb-text-muted)", marginBottom: 22 }}>
					Price categories. A fixture picks one and inherits every price in it, so you set this up once rather than fixture by fixture.
				</p>

				{isLoading && <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>}

				{!isLoading && <>
					<div className="pr-tabs">
						{templates.map((t) => (
							<button className={`pr-tab${selectedId === t.id ? " active" : ""}`} key={`t-${t.id}`} onClick={() => this.selectTemplate(t.id)} type="button">
								{t.name}
								{t.isDefault && <span className="pr-default">DEFAULT</span>}
								<small>{t.pricesSet} priced</small>
							</button>
						))}
						<Button className="p-button-text" icon="pi pi-plus" label="Add category" onClick={() => this.setState({ edit: { name: "", sortOrder: templates.length }, showDialog: true })} />
					</div>

					{templates.length === 0 &&
						<div className="pr-empty">
							<FontAwesomeIcon icon={faLayerGroup} />
							<h3>No price categories yet</h3>
							<p>Most clubs have two or three &mdash; League Category A, Category B, and a cup price. Add one and fill in its grid.</p>
						</div>}

					{selected && <>
						<div className="pr-toolbar">
							<div>
								<Button className="p-button-text" label="Edit category" onClick={() => this.setState({ edit: { ...selected }, showDialog: true })} />
								{templates.length > 1 &&
									<select
										className="pr-select"
										onChange={(e) => { if (e.target.value) this.copyFrom(Number(e.target.value)); e.target.value = ""; }}
										defaultValue=""
									>
										<option value="">Copy prices from&hellip;</option>
										{templates.filter((t) => t.id !== selected.id && t.pricesSet > 0)
											.map((t) => <option key={`cp-${t.id}`} value={t.id}>{t.name}</option>)}
									</select>}
								<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.removeTemplate(selected)} />
							</div>
							<div className="pr-save">
								{dirtyCount > 0 && <span className="pr-dirty-count">{dirtyCount} unsaved</span>}
								<Button disabled={!dirtyCount || this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save prices"} onClick={() => this.saveGrid()} />
							</div>
						</div>

						{this.renderGrid()}
					</>}
				</>}

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.saveTemplate()} />
				</>} header={this.state.edit?.id ? "Edit price category" : "New price category"} modal onHide={() => this.setState({ showDialog: false })} style={{ width: 440 }} visible={this.state.showDialog}>
					{this.state.edit && <>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ edit: { ...this.state.edit, name: e.target.value } })} placeholder="League: Category A" style={{ marginBottom: 16, width: "100%" }} value={this.state.edit.name || ""} />

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Season</label>
						<select className="pr-select" onChange={(e) => this.setState({ edit: { ...this.state.edit, seasonId: e.target.value ? Number(e.target.value) : null } })} style={{ marginBottom: 16, width: "100%" }} value={this.state.edit.seasonId ?? ""}>
							<option value="">All seasons</option>
							{seasons.map((s) => <option key={`ts-${s.id}`} value={s.id}>{s.name}</option>)}
						</select>

						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={!!this.state.edit.isDefault} onChange={(e) => this.setState({ edit: { ...this.state.edit, isDefault: e.value } })} />
							<label style={{ fontWeight: 600 }}>Use for new fixtures</label>
						</div>
					</>}
				</Dialog>
			</div>
		);
	}
}
